import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BaseMap } from '@/features/map-core';
import { ChoroplethLayer, HeatLayer, LayerSwitcher } from '@/features/choropleth';
import { useChoroplethStore } from '@/features/choropleth';
import { FiltersPanel, QuizProfileBanner } from '@/features/filters';
import { useFiltersStore } from '@/features/filters';
import { PoiPanel, PoiLayers, PoiAutoHint, usePoiStore } from '@/features/poi';
import { PlacesMapLayers, useFocusedIstats } from '@/features/places';
import { SearchBar } from '@/features/search';
import { DetailsPanel, useDetailsStore } from '@/features/details';
import { DistrictDetailsPanel } from '@/features/districts';
import { PinsLayer, AddPinDialog, BulkImportDialog, usePinsStore } from '@/features/pins';
import { VisitPlannerDialog } from '@/features/visit-planner';
import { MAP_COUNTRY_CONFIG, type MapCountry } from '@/features/map-core/countries';
import { AnnotationsLayer, AnnotationToolbar } from '@/features/annotations';
import { FeatureErrorBoundary } from '@/components/error/FeatureErrorBoundary';
import { AiHelperBubble } from '@/features/ai/AiHelperBubble';
import { useQuizStore, loadQuizState } from '@/features/quiz';
import { detectPersona } from '@/features/recommender/persona';

// Whitelist of internal routes the back-chip will navigate to. Anything
// else (external referrer, untrusted state) falls through to the safe
// default — never blind-redirect to an arbitrary URL even from history state.
const KNOWN_BACK_TARGETS = new Set([
  '/it/recommendations',
  '/quiz/result',
  '/countries',
  '/account',
]);

function inferBackTarget(stateFrom: unknown): string | null {
  if (typeof stateFrom === 'string' && KNOWN_BACK_TARGETS.has(stateFrom)) {
    return stateFrom;
  }
  // No router state — guess from the user's actual progress. Prefer
  // recommendations (the most recent stop in the funnel) and fall back
  // to quiz result if the quiz was completed at all.
  if (typeof window !== 'undefined') {
    try {
      const quiz = loadQuizState();
      if (quiz?.result) return '/it/recommendations';
    } catch {
      /* localStorage off — silent fall-through */
    }
  }
  return null;
}

function BackToContextChip() {
  const { t } = useTranslation();
  const location = useLocation();
  // Memoise the inference — useState lazy init runs once. The chip should
  // not change destination if the user toggles filters and useUrlSync
  // rewrites the hash later.
  const [target] = useState<string | null>(() => {
    const stateFrom = (location.state as { from?: string } | null)?.from;
    return inferBackTarget(stateFrom);
  });
  // When focus markers are on the map, slide the chip below the focus
  // banner (which sits at top: 0). Without focus, dock under the page
  // header chrome.
  const hasFocus = useFocusedIstats().size > 0;
  if (!target) return null;
  const label =
    target === '/it/recommendations'
      ? t('map.backTo.recommendations', { defaultValue: 'My recommendations' })
      : target === '/quiz/result'
        ? t('map.backTo.quizResult', { defaultValue: 'Quiz result' })
        : target === '/countries'
          ? t('map.backTo.countries', { defaultValue: 'Countries' })
          : t('map.backTo.account', { defaultValue: 'My account' });
  return (
    <Link
      to={target}
      data-testid="map-back-chip"
      className={[
        'absolute left-3 z-[1090] inline-flex items-center gap-1.5 rounded-full border bg-[color:var(--color-paper)]/95 px-3 py-1.5 font-mono text-[11px] font-medium uppercase tracking-wider shadow-md backdrop-blur-sm transition hover:bg-[color:var(--color-paper)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-terra-600)]',
        hasFocus
          ? 'top-12 border-[color:var(--color-terra-200)] text-[color:var(--color-terra-700)]'
          : 'top-3 border-[color:var(--color-ink-100)] text-[color:var(--color-ink-700)]',
      ].join(' ')}
    >
      <span aria-hidden="true">←</span>
      <span>{label}</span>
    </Link>
  );
}

interface MapPageProps {
  /**
   * Country of the `/<cc>/map` route this page renders. It comes from the
   * route (see MapWithHeader in App.tsx), which is the only place allowed to
   * decide which map is open — the same source `setActiveMapCountry` reads.
   *
   * It is threaded as a prop rather than read from module state because the
   * consumers below put it in front of the visitor or into stored data (the
   * AI context, the visit-planner email capture), where a wrong country is
   * invisible until someone audits the rows.
   */
  cc: MapCountry;
}

export default function MapPage({ cc }: MapPageProps) {
  const bulkOpen = usePinsStore((s) => s.bulkImportOpen);
  const closeBulk = usePinsStore((s) => s.closeBulkImport);
  // M5 — show the map-level AI bubble only when no comune is open; the
  // DetailsPanel mounts its own bubble with comune-specific context so two
  // bubbles overlapping at bottom-right would be confusing.
  const detailsOpen = useDetailsStore((s) => s.openIstat !== null);
  const activeLayer = useChoroplethStore((s) => s.activeLayer);
  const filters = useFiltersStore((s) => s.raw);
  const activePoi = usePoiStore((s) => s.active);
  const quizAnswers = useQuizStore((s) => s.answers);
  return (
    <div className="relative h-full w-full">
      <BaseMap>
        <FeatureErrorBoundary name="choropleth" silent>
          <ChoroplethLayer />
        </FeatureErrorBoundary>
        <FeatureErrorBoundary name="heatmap" silent>
          <HeatLayer />
        </FeatureErrorBoundary>
        <FeatureErrorBoundary name="poi" silent>
          <PoiLayers />
        </FeatureErrorBoundary>
        <FeatureErrorBoundary name="places-map" silent>
          <PlacesMapLayers />
        </FeatureErrorBoundary>
        <FeatureErrorBoundary name="pins" silent>
          <PinsLayer />
        </FeatureErrorBoundary>
        <FeatureErrorBoundary name="annotations" silent>
          <AnnotationsLayer />
        </FeatureErrorBoundary>
      </BaseMap>

      <FeatureErrorBoundary name="search">
        <SearchBar />
      </FeatureErrorBoundary>
      <FeatureErrorBoundary name="filters">
        <FiltersPanel />
      </FeatureErrorBoundary>
      <FeatureErrorBoundary name="layer-switcher">
        <LayerSwitcher />
      </FeatureErrorBoundary>
      <FeatureErrorBoundary name="poi-panel">
        <PoiPanel />
      </FeatureErrorBoundary>
      <FeatureErrorBoundary name="poi-auto-hint" silent>
        <PoiAutoHint />
      </FeatureErrorBoundary>
      <FeatureErrorBoundary name="details">
        <DetailsPanel />
      </FeatureErrorBoundary>
      <FeatureErrorBoundary name="district-details">
        <DistrictDetailsPanel />
      </FeatureErrorBoundary>
      <FeatureErrorBoundary name="pins-dialog">
        <AddPinDialog />
      </FeatureErrorBoundary>
      <FeatureErrorBoundary name="pins-bulk-dialog">
        <BulkImportDialog open={bulkOpen} onClose={closeBulk} />
      </FeatureErrorBoundary>
      <FeatureErrorBoundary name="visit-planner">
        {/* The unlock form captures an email; it must be filed under the map
            the visitor is on, not under whichever country shipped first. */}
        <VisitPlannerDialog countryCode={cc.toUpperCase()} />
      </FeatureErrorBoundary>
      <FeatureErrorBoundary name="annotations-toolbar">
        <AnnotationToolbar />
      </FeatureErrorBoundary>
      <FeatureErrorBoundary name="quiz-profile-banner" silent>
        <QuizProfileBanner />
      </FeatureErrorBoundary>
      <FeatureErrorBoundary name="back-chip" silent>
        <BackToContextChip />
      </FeatureErrorBoundary>
      {!detailsOpen && (
        <FeatureErrorBoundary name="ai-helper-map" silent>
          <AiHelperBubble
            surface="map"
            placement="br-map"
            context={{
              // Same reason as the visit planner: the assistant was told it
              // was looking at Italy on every map.
              country: MAP_COUNTRY_CONFIG[cc].nameEn,
              activeLayer,
              activePoi: Array.from(activePoi),
              filters: {
                altitude: [filters.alt_min, filters.alt_max],
                t_jul_max: filters.t_jul_max,
                t_jan_min: filters.t_jan_min,
                hum_max: filters.hum_max,
                snow_min: filters.snow_min,
                sunny_min: filters.sunny_min,
                pop_max: filters.pop_max,
                hosp_max: filters.hosp_max,
                zones: Array.from(filters.zones),
              },
              persona: detectPersona(quizAnswers),
            }}
          />
        </FeatureErrorBoundary>
      )}
    </div>
  );
}
